import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { useLocation } from '@reach/router';
import { navigate } from 'gatsby';
import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FORM_PAGES } from '../../../components/Form/types';
import { FORM_INFO_SLOT_TYPES } from '../../../components/FormInfo/types';
import FormRadioProducts from '../../../components/FormRadio/Products/FormRadio';
import { FORM_RADIO_TYPES } from '../../../components/FormRadio/types';
import useUserProfile from '../../../hooks/useUserProfile';
import { vars } from '../../../reactive';
import { increaseFormPage, setFormPage } from '../../../reactive/actions';
import { checkProductSubscriptionsDependecies, clearLocalStorage, kebabCase } from '../../../utils';
import { AnalyticsEvents, trackEvent } from '../../../utils/analytics';
import { GetAllUserOrderSubscriptions } from '../../../utils/queries';

const FormProducts = () => {
  const [getProfile, { loading: userProfileLoading }] = useUserProfile(true);
  const [getAllUserOrderSubscriptions, { data: { getAllUserOrderSubscriptions: subscriptions } = {} }] = useLazyQuery(GetAllUserOrderSubscriptions, {
    fetchPolicy: 'no-cache'
  });

  const { register, handleSubmit, reset, formState, watch } = useForm();
  const watchedProducts = watch('products');
  const location = useLocation();
  const selectedProductQueryParam = useMemo(() => new URLSearchParams(location.search).get('product'), [location.search]);
  const currentProvider = vars.currentProvider();
  const currentBranch = vars.currentBranch();

  const isProviderView = vars.isProviderView();
  const isLoggedIn = useReactiveVar(vars.isLoggedIn);

  useEffect(() => {
    if (isLoggedIn) {
      getAllUserOrderSubscriptions();
    }
  }, [isLoggedIn]);

  const allProducts = isProviderView ? currentProvider?.Branches?.flatMap(branch => branch?.visibleProducts || []) : currentBranch?.visibleProducts || [];

  const selectedBranch = currentBranch ?? currentProvider?.Branches?.[0];

  const products = (allProducts || [])
    .filter(product => product?.type === 'service' || product?.type === 'subscription')
    .filter(product => product?.status !== 'HIDDEN' && product?.status !== 'ARCHIVED')
    .slice()
    .sort(sortBySortIndex) as (Queries.CollarAPI_Product & { title?: string })[];

  const productCategories = Array.from(new Set(products?.flatMap(product => product?.ProductCategories || []).map(category => category?.id))).map(id => {
    const category = products?.flatMap(product => product?.ProductCategories || []).find(cat => cat.id === id);
    return { name: category?.name?.toLowerCase(), id: category?.id };
  });

  const subscriptionReliantServices = products.filter(product => !checkProductSubscriptionsDependecies(product!, subscriptions));

  const kebabCaseProducts = products?.map(product => ({
    ...product,
    name: product?.slugs?.path || kebabCase(product!.name.trimEnd().trimStart())
  }));

  const preSelectedProduct = kebabCaseProducts?.find(product => product.name.toLowerCase() === selectedProductQueryParam?.toLowerCase());

  const checkIfAvailable = (productId: string) => {
    const product = products?.find(product => product?.id === productId);
    return product?.status === 'IN_STOCK' && product?.stock !== 0;
  };

  const onSubmit = async (data: Record<string, any>) => {
    const selectedProduct = products?.filter(product => product!.id === data.products)[0];
    clearLocalStorage();

    const newBranch = isProviderView ? currentProvider?.Branches?.find(branch => branch?.visibleProducts?.find(product => product?.id === selectedProduct?.id)) : currentBranch;

    vars.currentBranch(newBranch);
    vars.selectedProduct(selectedProduct);

    trackEvent(AnalyticsEvents.SERVICE_SELECTED, {
      name: selectedProduct?.name
    });

    if (selectedProduct?.hasOwnProperty('customizations') && Object.keys(selectedProduct?.customizations).length) {
      return increaseFormPage();
    }

    const multiSlotProduct = selectedProduct?.booking_type === FORM_INFO_SLOT_TYPES.MULTI_SLOT;

    if (multiSlotProduct || selectedProduct?.type === 'subscription') {
      if (vars.isLoggedIn()) {
        await getProfile();
      } else {
        setFormPage(FORM_PAGES.CREATE_ACCOUNT_EMAIL);
      }
      return;
    }

    if (selectedProduct?.allows_repeated_orders) {
      vars.recurrenceType('one_off');
      vars.selectedOrderGroupSlots([{ slots: [] }]);
      setFormPage(FORM_PAGES.BOOKING_CALENDAR_BOOKING_TYPE);
      return;
    }

    setFormPage(FORM_PAGES.BOOKING_CALENDAR_DATE_AND_TIME);
  };

  useEffect(() => {
    if (watchedProducts) {
      handleSubmit(onSubmit)();
    }
  }, [watchedProducts]);

  useEffect(() => {
    if (selectedProductQueryParam) {
      (async () => {
        await handleSubmit(onSubmit)();
        navigate(location.pathname);
      })();
    }
  }, [selectedProductQueryParam]);

  return (
    <FormRadioProducts
      type={FORM_RADIO_TYPES.PRODUCTS}
      formOptions={{ register, handleSubmit, reset, formState }}
      checkIfAvailable={checkIfAvailable}
      dataArr={products}
      borderColor={selectedBranch?.colors?.secondary}
      loading={userProfileLoading}
      defaultCheckedId={preSelectedProduct?.id}
      isLoggedIn={isLoggedIn}
      disabledProducts={subscriptionReliantServices}
      noNextButton
      categoriesOptions={productCategories}
    />
  );
};

export default FormProducts;

function sortBySortIndex(c1: Queries.CollarAPI_Product, c2: Queries.CollarAPI_Product) {
  return c2.sort_index === c1.sort_index ? 0 : c2.sort_index > c1.sort_index ? -1 : 1;
}
